/**
 * Per-slot retry: after a video consumer attaches, keep asking for a fresh keyframe for
 * a few seconds so a genuine GOP arrives even if the very first PLI was lost. Uses the
 * same generation-guard shape as `useOperatorSession.ts`'s React-side generation guard —
 * scheduling a NEW retry for a slot invalidates any of that slot's still-pending retries
 * from before, so a stale retry can never fire after the slot has moved on (e.g. the
 * consumer was replaced again in the meantime).
 */

const SLOT_RESTORE_DELAYS_MS = [800, 2000, 4000];

export interface CreateSlotRestoreSchedulerDeps {
  delays?: number[];
  isEnabled: (slot: number) => boolean;
  isReady: () => boolean;
  onRestore: (slot: number) => void;
}

export function createSlotRestoreScheduler({
  delays = SLOT_RESTORE_DELAYS_MS,
  isEnabled,
  isReady,
  onRestore,
}: CreateSlotRestoreSchedulerDeps) {
  const generationBySlot = new Map<number, number>();

  function scheduleSlotRestore(slot: number) {
    if (slot < 0) return;
    const gen = (generationBySlot.get(slot) ?? 0) + 1;
    generationBySlot.set(slot, gen);
    const tryOnce = () => {
      if (generationBySlot.get(slot) !== gen) return;
      if (!isEnabled(slot)) return;
      if (!isReady()) return; // no RTP before ICE; the caller's own "ready" burst covers this
      onRestore(slot);
    };
    for (const delay of delays) {
      setTimeout(() => tryOnce(), delay);
    }
  }

  /** Cancel every slot's still-pending retries (e.g. on full pipeline teardown). */
  function invalidateSlotRestores() {
    for (const slot of generationBySlot.keys()) {
      generationBySlot.set(slot, (generationBySlot.get(slot) ?? 0) + 1);
    }
  }

  return { scheduleSlotRestore, invalidateSlotRestores };
}

export type SlotRestoreScheduler = ReturnType<typeof createSlotRestoreScheduler>;
